/**
 * Modal dialog — admin invites a user by email + role + optional message.
 *
 * Submits to POST /api/admin/invitations and invalidates the ["invitations"]
 * query on success.
 */
import * as Dialog from "@radix-ui/react-dialog";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { extractApiError } from "../../lib/api";
import { createInvitation } from "../../lib/dataFetchers";
import { Role } from "../../lib/permissions";
import { Button } from "../ui/Button";

interface Props {
  open: boolean;
  onClose: () => void;
}

// Admin is intentionally NOT invitable here. To grant admin you invite the
// person as legal_advisor or user first, then promote them from /admin.
// Backend `InvitationCreate` enforces the same rule.
const ROLES: { value: Role; label: string }[] = [
  { value: "user", label: "Member" },
  { value: "legal_advisor", label: "Legal Advisor" },
];

export function InviteUserDialog({ open, onClose }: Props): JSX.Element {
  const qc = useQueryClient();
  const { t } = useTranslation(["admin", "common"]);

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("user");
  const [message, setMessage] = useState("");

  const mutation = useMutation({
    mutationFn: () =>
      createInvitation({
        email: email.trim().toLowerCase(),
        role,
        personalMessage: message.trim() || null,
      }),
    onSuccess: () => {
      toast.success(
        t("admin:invitation_created", { defaultValue: "Invitation sent" }),
      );
      void qc.invalidateQueries({ queryKey: ["invitations"] });
      handleClose();
    },
    onError: (err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        toast.error(
          t("admin:invitation_user_exists", {
            defaultValue:
              "An active user already exists for this email — use the role controls instead.",
          }),
        );
      } else {
        toast.error(extractApiError(err));
      }
    },
  });

  function handleClose(): void {
    setEmail("");
    setRole("user");
    setMessage("");
    onClose();
  }

  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[min(92vw,480px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-surface border border-border p-6 shadow-xl focus:outline-none"
          onPointerDownOutside={(e) => mutation.isPending && e.preventDefault()}
        >
          <Dialog.Title className="text-[16px] font-semibold text-text mb-1">
            {t("admin:invite_user", { defaultValue: "Invite a user" })}
          </Dialog.Title>
          <Dialog.Description className="text-[13px] text-dim mb-4">
            {t("admin:invite_user_description", {
              defaultValue:
                "Send an invitation email. The invitee signs in with their Google account at the invited email.",
            })}
          </Dialog.Description>

          <div className="space-y-4">
            <label className="block">
              <span className="block text-[12.5px] font-medium text-text mb-1">
                {t("common:email", { defaultValue: "Email" })}
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                placeholder="name@example.com"
                className="w-full rounded-lg border border-border bg-surface-2 px-3 py-2 text-base focus:bg-surface focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/14"
              />
            </label>

            <label className="block">
              <span className="block text-[12.5px] font-medium text-text mb-1">
                {t("admin:role", { defaultValue: "Role" })}
              </span>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
                className="w-full rounded-lg border border-border bg-surface-2 px-3 py-2 text-base focus:bg-surface focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/14"
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              <span className="block text-[11px] text-dim mt-1">
                {t("admin:admin_promote_only", {
                  defaultValue:
                    "Admin can only be granted by promoting an existing user from the table below.",
                })}
              </span>
            </label>

            <label className="block">
              <span className="block text-[12.5px] font-medium text-text mb-1">
                {t("admin:personal_message", {
                  defaultValue: "Personal message (optional)",
                })}
              </span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder={t("admin:personal_message_placeholder", {
                  defaultValue:
                    "A short note that will appear in the invitation email.",
                })}
                className="w-full rounded-lg border border-border bg-surface-2 px-3 py-2 text-base focus:bg-surface focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/14"
              />
              <span className="block text-[11px] text-dim mt-1">
                {message.length}/500
              </span>
            </label>
          </div>

          <div className="mt-5 flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={mutation.isPending}
            >
              {t("common:cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              type="button"
              onClick={() => mutation.mutate()}
              disabled={!valid || mutation.isPending}
            >
              {mutation.isPending
                ? t("common:sending", { defaultValue: "Sending..." })
                : t("admin:send_invitation", { defaultValue: "Send invitation" })}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
